import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { VertexBuffer } from '@babylonjs/core/Buffers/buffer';

import type { ArmourClass, OffHandClass, WeaponClass } from '../../net/protocol';
import type { PropId, PropLibrary } from './props';
import type { Rig } from './rig';
import type { WornGear } from './types';

/**
 * Generated gear on the painted bodies: the helmet, the weapon and the off hand, from models made
 * out of the items' own inventory icons (Phase 28.1, `architecture/GENERATED-PROPS.md` §9).
 *
 * **One model per family, not per item** — the snapshot carries the family (`weapon_type`,
 * `offhand_type`, the head's armour class), deliberately (ADR 0016), and at ~70 px a family is what
 * reads. **Painted bodies only**: the box figures keep their procedural gear, which was drawn for
 * them. And **a procedural piece is only hidden once its model is placed**, so a missing file costs
 * the look it had, never the item.
 *
 * ── How a model is fitted ──────────────────────────────────────────────────────────────────
 *
 * Every model is fitted to **the procedural geometry it replaces**, in that geometry's own joint
 * frame — the grip, the forearm, the head — so it swings with exactly the pose the procedural piece
 * did, and a new weapon family needs no hand-placed numbers:
 *
 * - **A weapon**: long axis to long axis, length to length, the handle end where the handle was.
 *   The icons all draw the tip or head at the **top right**, and Hunyuan keeps the image's
 *   orientation, so a model's tip is the end of its long axis that points along (+x, +y).
 * - **A flat thing** (shield, tome): face normal to face normal, long side to long side, size and
 *   centre to theirs. An icon faces the viewer, so a model's face is its +Z.
 * - **A round thing** (orb): centre and size.
 * - **A helmet**: on the painted head, not the box head it replaced — its width a little over the
 *   head's, its crown just above the head's top. Its front is +Z, as the body's is.
 */

/**
 * A weapon family's model. **Both axes share the greataxe**: the game's icons have no one-handed
 * axe of their own, and the fit sizes it to the procedural hand-axe, so it only has to read as an
 * axe. Families not listed keep their procedural weapon — and three of those were generated and
 * **rejected by looking** (2026-09-28): the staff came out a thick, cloth-like lump, and the bow and
 * wand, fitted right, were thin dark lines that vanish at game scale where the procedural ones'
 * pale limbs and lit tips are what make a caster read as one.
 */
export const GEAR_WEAPON: Readonly<Partial<Record<WeaponClass, PropId>>> = {
  SWORD_1H: 'gear_weapon_sword_1h',
  SWORD_2H: 'gear_weapon_sword_2h',
  AXE_1H: 'gear_weapon_axe_2h',
  AXE_2H: 'gear_weapon_axe_2h',
  MACE_1H: 'gear_weapon_mace_1h',
  MACE_2H: 'gear_weapon_mace_2h',
  DAGGER: 'gear_weapon_dagger',
};
/** An off hand's model. The second dagger is a weapon, drawn procedurally as the off hand's. */
export const GEAR_OFFHAND: Readonly<Partial<Record<OffHandClass, PropId>>> = {
  BUCKLER: 'gear_offhand_buckler',
  SHIELD: 'gear_offhand_shield',
  TOME: 'gear_offhand_tome',
  ORB: 'gear_offhand_orb',
};
export const GEAR_HEAD: Readonly<Record<ArmourClass, PropId>> = {
  CLOTH: 'gear_head_cloth',
  LEATHER: 'gear_head_leather',
  PLATE: 'gear_head_plate',
};

/**
 * How each helmet sits: `width` over the painted head's (it sits over hair, not on the scalp), and
 * `lift`, the crown's height above the head's top. **Chosen by looking**, per file: one ratio for
 * all three put the leather cap's rim over the eyes, because a cap is taller for its width than a
 * helm is.
 */
export const HELMET_FIT: Readonly<Record<ArmourClass, { width: number; lift: number }>> = {
  CLOTH: { width: 1.35, lift: 0.03 },
  LEATHER: { width: 1.12, lift: 0.05 },
  PLATE: { width: 1.35, lift: 0.03 },
};

type FitMode = 'weapon' | 'flat' | 'round';

/**
 * Points of `meshes` in `frame`'s local space: their vertices, or their bounding-box corners for a
 * mesh with none. **Vertices, not corners**: a model lying on its file's diagonal has a square box,
 * and that box turned upright measures √2 too wide.
 */
function cornersIn(frame: TransformNode, meshes: readonly AbstractMesh[]): Vector3[] {
  const toFrame = frame.computeWorldMatrix(true).clone().invert();
  const out: Vector3[] = [];
  for (const m of meshes) {
    const toLocal = m.computeWorldMatrix(true).multiply(toFrame);
    const p = m.getVerticesData(VertexBuffer.PositionKind);
    if (p && p.length > 0) {
      for (let i = 0; i < p.length; i += 3) out.push(Vector3.TransformCoordinates(new Vector3(p[i]!, p[i + 1]!, p[i + 2]!), toLocal));
    } else {
      for (const c of m.getBoundingInfo().boundingBox.vectors) out.push(Vector3.TransformCoordinates(c, toLocal));
    }
  }
  return out;
}

/** The model's vertices in its own file frame. */
function vertices(meshes: readonly Mesh[]): Vector3[] {
  const out: Vector3[] = [];
  for (const m of meshes) {
    const p = m.getVerticesData(VertexBuffer.PositionKind);
    if (!p) continue;
    for (let i = 0; i < p.length; i += 3) out.push(new Vector3(p[i]!, p[i + 1]!, p[i + 2]!));
  }
  return out;
}

/** Centre, and the three principal axes, largest spread first. Power iteration on the covariance. */
function principal(points: readonly Vector3[]): { centre: Vector3; axes: Vector3[]; spread: number[] } {
  const c = new Vector3();
  for (const p of points) c.addInPlace(p);
  c.scaleInPlace(1 / Math.max(1, points.length));
  const cov = [0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (const p of points) {
    const d = [p.x - c.x, p.y - c.y, p.z - c.z];
    for (let i = 0; i < 3; i++) for (let j = 0; j < 3; j++) cov[i * 3 + j]! += d[i]! * d[j]!;
  }
  const axes: Vector3[] = [];
  const spread: number[] = [];
  const m = cov.slice();
  for (let k = 0; k < 3; k++) {
    // Seeded from whichever basis vector is least covered by the axes found so far: a seed parallel
    // to one of them (a cube's covariance keeps any vector) would orthogonalise to nothing.
    const residual = (u: Vector3) => axes.reduce((r, a) => r.subtract(a.scale(Vector3.Dot(r, a))), u.clone());
    let v = [new Vector3(1, 0.7, 0.3), new Vector3(-0.3, 1, 0.5), new Vector3(0.2, -0.4, 1)]
      .map(residual)
      .reduce((best, u) => (u.length() > best.length() ? u : best));
    v.normalize();
    for (let it = 0; it < 60; it++) {
      let n = new Vector3(m[0]! * v.x + m[1]! * v.y + m[2]! * v.z, m[3]! * v.x + m[4]! * v.y + m[5]! * v.z, m[6]! * v.x + m[7]! * v.y + m[8]! * v.z);
      for (const a of axes) n = n.subtract(a.scale(Vector3.Dot(n, a)));
      const len = n.length();
      if (len < 1e-12) break;
      v = n.scale(1 / len);
    }
    axes.push(v);
    let s = 0;
    for (const p of points) s += Vector3.Dot(p.subtract(c), v) ** 2;
    spread.push(s);
  }
  return { centre: c, axes, spread };
}

function extent(points: readonly Vector3[], axis: Vector3): [number, number] {
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of points) {
    const d = Vector3.Dot(p, axis);
    lo = Math.min(lo, d);
    hi = Math.max(hi, d);
  }
  return [lo, hi];
}

/** The rotation carrying unit `a` onto unit `b`, the shortest way round. */
function between(a: Vector3, b: Vector3): Quaternion {
  const d = Vector3.Dot(a, b);
  if (d > 0.999999) return Quaternion.Identity();
  if (d < -0.999999) {
    const ortho = Math.abs(a.x) < 0.9 ? Vector3.Right() : Vector3.Up();
    return Quaternion.RotationAxis(Vector3.Cross(a, ortho).normalize(), Math.PI);
  }
  const axis = Vector3.Cross(a, b).normalize();
  return Quaternion.RotationAxis(axis, Math.acos(Math.max(-1, Math.min(1, d))));
}

const rotate = (q: Quaternion, v: Vector3) => v.applyRotationQuaternion(q);

const mean = (pts: readonly Vector3[]) => pts.reduce((sum, p) => sum.add(p), Vector3.Zero()).scale(1 / Math.max(1, pts.length));

/**
 * A long shape's chord along roughly `dir`: the middles of its two ends (the points within 5 % of
 * each extreme), the unit axis from one to the other, and `side` — how far the shape's mean sits
 * off that line, which is where a curved thing bends.
 */
function chord(points: readonly Vector3[], dir: Vector3): { butt: Vector3; tip: Vector3; axis: Vector3; side: Vector3 } {
  const [lo, hi] = extent(points, dir);
  const band = (hi - lo) * 0.05;
  const butt = mean(points.filter((p) => Vector3.Dot(p, dir) <= lo + band));
  const tip = mean(points.filter((p) => Vector3.Dot(p, dir) >= hi - band));
  const axis = tip.subtract(butt).normalize();
  const offset = mean(points).subtract(butt);
  return { butt, tip, axis, side: offset.subtract(axis.scale(Vector3.Dot(offset, axis))) };
}

/**
 * Places `meshes` (the model, in its file frame) under a new node in `frame`, fitted to `target` —
 * the procedural parts it replaces, which must already hang in `frame`'s subtree.
 */
export function fitTo(
  frame: TransformNode,
  target: readonly AbstractMesh[],
  model: readonly Mesh[],
  mode: FitMode,
  name: string,
): TransformNode | null {
  const t = cornersIn(frame, target);
  const m = vertices(model);
  if (t.length === 0 || m.length === 0) return null;
  const tp = principal(t);
  const mp = principal(m);

  let rotation: Quaternion;
  let scale: number;
  // Where the model is anchored: by default centre to centre, a weapon handle to handle.
  let anchor: { t: Vector3; m: Vector3 } | null = null;
  if (mode === 'weapon') {
    // The target's axis runs **from the hand to its farthest point**, not along its widest spread:
    // a one-handed mace is a short haft under a ball wider than the haft is long, so its principal
    // axis is any direction at all, and the model came out a quarter of the size, pointing sideways.
    // The frame's origin is the hand. The model's tip is its top right.
    // Aimed at the middle of the far end — every point within 5 % of the farthest, **on its side of
    // the hand** — not at one corner of it, which tilts a blade off its own centre line by half its
    // width. The side matters for a bow, held at its middle: both tips are equally far, and
    // averaging the two cancelled its length and left its curve as the axis.
    const farthest = t.reduce((best, p) => (p.lengthSquared() > best.lengthSquared() ? p : best));
    const reach = farthest.length();
    const tip = mean(t.filter((p) => p.length() >= reach * 0.95 && Vector3.Dot(p, farthest) > 0));
    // Then **along the chord**, from the middle of the near end to the middle of the far one: a
    // bow's tips curve forward of its grip, and the line from the hand to one tip leant it 24°.
    const tc = chord(t, tip.normalizeToNew());
    const tAxis = tc.axis;
    const mDir = mp.axes[0]!.clone();
    if (mDir.x + mDir.y < 0) mDir.scaleInPlace(-1);
    const mc = chord(m, mDir);
    const mAxis = mc.axis;
    const align = between(mAxis, tAxis);
    // **Then the roll about the length**, which nothing above fixes. By the bend where there is one
    // — a bow's limbs curve forward of the chord, and the procedural bow's do — and otherwise by
    // the width, so a blade's flat lies where the procedural blade's did. The first bow lay with
    // its curve out to the side.
    const across = (v: Vector3) => v.subtract(tAxis.scale(Vector3.Dot(v, tAxis)));
    const length = Vector3.Distance(tc.tip, tc.butt);
    let tSide = across(tc.side);
    let mSide = across(rotate(align, mc.side.clone()));
    if (tSide.length() < length * 0.03 || mSide.length() < Vector3.Distance(mc.tip, mc.butt) * 0.03) {
      tSide = across(tp.axes[1]!);
      mSide = across(rotate(align, mp.axes[1]!.clone()));
      if (Vector3.Dot(tSide, mSide) < 0) mSide.scaleInPlace(-1);
    }
    // A signed turn about the length itself — `between` would pick any axis for a half turn.
    const roll = tSide.length() > 1e-6 && mSide.length() > 1e-6
      ? Math.atan2(Vector3.Dot(Vector3.Cross(mSide, tSide), tAxis), Vector3.Dot(mSide, tSide))
      : 0;
    rotation = Quaternion.RotationAxis(tAxis, roll).multiply(align);
    // Length to length, and **handle to handle**: the model's butt where the procedural butt was,
    // so a longer or lopsided model grows away from the hand, not both ways.
    scale = length / Math.max(1e-6, Vector3.Distance(mc.tip, mc.butt));
    anchor = { t: tc.butt, m: mc.butt };
  } else if (mode === 'flat') {
    // Face to face: the target's thinnest axis, pointing away from the joint; the model's +Z.
    const tNormal = tp.axes[2]!.clone();
    if (Vector3.Dot(tNormal, tp.centre) < 0) tNormal.scaleInPlace(-1);
    const mNormal = mp.axes[2]!.clone();
    if (mNormal.z < 0) mNormal.scaleInPlace(-1);
    const r1 = between(mNormal, tNormal);
    // Then turn about the normal so the long sides agree.
    const mUp = rotate(r1, mp.axes[0]!.clone());
    const tUp = tp.axes[0]!.clone();
    if (Vector3.Dot(mUp, tUp) < 0) tUp.scaleInPlace(-1);
    const r2 = between(mUp.subtract(tNormal.scale(Vector3.Dot(mUp, tNormal))).normalize(), tUp.subtract(tNormal.scale(Vector3.Dot(tUp, tNormal))).normalize());
    rotation = r2.multiply(r1);
    const [a0, a1] = extent(t, tp.axes[0]!);
    const [b0, b1] = extent(m, mp.axes[0]!);
    scale = (a1 - a0) / Math.max(1e-6, b1 - b0);
  } else {
    rotation = Quaternion.Identity();
    const size = (pts: readonly Vector3[]) => Math.max(...[Vector3.Right(), Vector3.Up(), Vector3.Forward()].map((ax) => { const [lo, hi] = extent(pts, ax); return hi - lo; }));
    scale = size(t) / Math.max(1e-6, size(m));
  }

  // Centre to centre — the midpoints of their boxes along the principal axes, which a lopsided
  // model (an axe's head) does not drag the way a vertex mean would.
  const mid = (pts: readonly Vector3[], p: ReturnType<typeof principal>) =>
    p.axes.reduce((acc, ax) => { const [lo, hi] = extent(pts, ax); return acc.add(ax.scale((lo + hi) / 2)); }, Vector3.Zero());
  const tc = anchor?.t ?? mid(t, tp);
  const mc = anchor?.m ?? mid(m, mp);

  const holder = new TransformNode(name, frame.getScene());
  holder.parent = frame;
  holder.rotationQuaternion = rotation;
  holder.scaling.setAll(scale);
  holder.position.copyFrom(tc.subtract(rotate(rotation, mc.scale(scale))));
  for (const mesh of model) {
    mesh.parent = holder;
    mesh.position.setAll(0);
    mesh.rotation.setAll(0);
    mesh.rotationQuaternion = null;
    mesh.scaling.setAll(1);
    mesh.isPickable = false;
  }
  return holder;
}

/**
 * The helmet, on the painted head. `headPoints` are the body's head vertices in `rig.head`'s frame
 * (bind pose), which is the shape the helmet has to sit over.
 */
export function fitHelmet(head: TransformNode, headPoints: readonly Vector3[], model: readonly Mesh[], fit: { width: number; lift: number }, name: string): TransformNode | null {
  const m = vertices(model);
  if (headPoints.length === 0 || m.length === 0) return null;
  const box = (pts: readonly Vector3[]) => {
    const lo = new Vector3(Infinity, Infinity, Infinity);
    const hi = new Vector3(-Infinity, -Infinity, -Infinity);
    for (const p of pts) { lo.minimizeInPlace(p); hi.maximizeInPlace(p); }
    return { lo, hi };
  };
  const hb = box(headPoints);
  const mb = box(m);
  const scale = ((hb.hi.x - hb.lo.x) * fit.width) / Math.max(1e-6, mb.hi.x - mb.lo.x);
  const holder = new TransformNode(name, head.getScene());
  holder.parent = head;
  holder.scaling.setAll(scale);
  // Crown a touch above the head's top; centred over it in x and z.
  holder.position.set(
    (hb.lo.x + hb.hi.x) / 2 - ((mb.lo.x + mb.hi.x) / 2) * scale,
    hb.hi.y + fit.lift - mb.hi.y * scale,
    (hb.lo.z + hb.hi.z) / 2 - ((mb.lo.z + mb.hi.z) / 2) * scale,
  );
  for (const mesh of model) {
    mesh.parent = holder;
    mesh.position.setAll(0);
    mesh.rotation.setAll(0);
    mesh.rotationQuaternion = null;
    mesh.scaling.setAll(1);
    mesh.isPickable = false;
  }
  return holder;
}

/** What a character wears, for the gear it can show. */
export interface GearSpec {
  weapon?: WeaponClass;
  offhand?: OffHandClass;
  head?: ArmourClass;
}

/**
 * The gear spec from what `create` is handed. **A helmet only for a head item actually worn** —
 * chosen: the box rig falls back to the chest's class (`headStyleFor`) so every figure wears
 * *something*, but a painted body has painted hair, and a bare head is what an empty slot is.
 */
export function gearSpec(weapon: string | undefined, worn: WornGear | undefined): GearSpec {
  return { weapon: weapon as WeaponClass | undefined, offhand: worn?.offhand, head: worn?.head };
}

/**
 * Dresses a painted body in its generated gear, hiding each procedural piece a model replaced.
 * `headPoints` come from the skinned body (see `SkinnedBody.headPoints`). Returns the meshes placed.
 */
export function dressGear(rig: Rig, props: PropLibrary, gear: GearSpec, headPoints: readonly Vector3[], entity: string): Mesh[] {
  const placed: Mesh[] = [];
  const take = (id: PropId | undefined) => (id ? props.instantiate(id, `${entity}-${id}`) : null);

  if (gear.head) {
    const model = take(GEAR_HEAD[gear.head]);
    if (model && fitHelmet(rig.head, headPoints, model, HELMET_FIT[gear.head], `${entity}-helmet`)) placed.push(...model);
  }

  const weaponParts = rig.heldParts.filter((p) => isUnder(p, rig.grip));
  const weapon = take(gear.weapon ? GEAR_WEAPON[gear.weapon] : undefined);
  if (weapon && weaponParts.length > 0 && fitTo(rig.grip, weaponParts, weapon, 'weapon', `${entity}-weapon-model`)) {
    for (const p of weaponParts) p.isVisible = false;
    placed.push(...weapon);
  }

  const offParts = rig.heldParts.filter((p) => p.name.includes('off-'));
  const offId = gear.offhand ? GEAR_OFFHAND[gear.offhand] : undefined;
  const off = take(offId);
  const frame = offParts[0]?.parent as TransformNode | undefined;
  if (off && frame && offParts.length > 0) {
    const mode: FitMode = gear.offhand === 'ORB' ? 'round' : 'flat';
    if (fitTo(frame, offParts, off, mode, `${entity}-offhand-model`)) {
      for (const p of offParts) p.isVisible = false;
      placed.push(...off);
    }
  }
  return placed;
}

function isUnder(mesh: AbstractMesh, node: TransformNode): boolean {
  for (let p = mesh.parent; p; p = p.parent) if (p === node) return true;
  return false;
}

/** Used by tests: the model's frame-local box after fitting. */
export function boxIn(frame: TransformNode, meshes: readonly AbstractMesh[]): { lo: Vector3; hi: Vector3 } {
  const pts = cornersIn(frame, meshes);
  const lo = new Vector3(Infinity, Infinity, Infinity);
  const hi = new Vector3(-Infinity, -Infinity, -Infinity);
  for (const p of pts) { lo.minimizeInPlace(p); hi.maximizeInPlace(p); }
  return { lo, hi };
}

